import type { OrganizationAdministrationContext } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/config"
import type {
  AdministrationConfig,
  AdministrationSubviewConfig,
} from "@kontextwerk/control-web-frame/components/administration"
import { subviewConfig as sharedAgents } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/Agents/config"
import { subviewConfig as sharedBilling } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/Billing/config"
import {
  aiRulesSubviewConfig as sharedAiRules,
  phoneNumbersSubviewConfig as sharedPhoneNumbers,
} from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/config"
import { subviewConfig as sharedContactProfiles } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/ContactProfiles/config"
import { subviewConfig as sharedLegalDocuments } from "@kontextwerk/control-web-frame/routes/OrgaView/pages/Administration/subviews/LegalDocuments/config"
import type { OrgaViewFrameContext } from "../../context"
import { subviewConfig as organization } from "./subviews/Organization/config"
import { subviewConfig as users } from "./subviews/Users/config"
import { subviewConfig as orderTypes } from "./subviews/OrderTypes/config"
import { subviewConfig as auditLog } from "./subviews/AuditLog/config"
import { subviewConfig as district } from "./subviews/District/config"
import { subviewConfig as customers } from "./subviews/Customers/config"
import { subviewConfig as properties } from "./subviews/Properties/config"
import { subviewConfig as dailyBriefing } from "./subviews/DailyBriefing/config"
import { subviewConfig as planning } from "./subviews/Planning/config"
import { subviewConfig as announcementTemplates } from "./subviews/AnnouncementTemplates/config"
import {
  canViewOrganizationSettings,
  isSubscriptionRestricted,
  canViewBilling,
} from "../../_shared/_logic/access"

const sharedContext = (context: OrgaViewFrameContext): OrganizationAdministrationContext => ({
  permissions: context.permissions,
  subscriptionExpired: context.subscriptionRestrictionActive,
  billingMode: context.billingMode,
  billingAvailable: context.billingAvailable,
  billingEnabled: context.billingEnabled,
  billingRequiresCheckout: context.billingRequiresCheckout,
  hasHistoricalConsumption: context.hasHistoricalConsumption,
  currentUserRole: context.currentUserRole,
  feedbackEnabled: !isSubscriptionRestricted(context),
})

const withAccess = (
  config: AdministrationSubviewConfig<OrganizationAdministrationContext>,
  allowed: (context: OrgaViewFrameContext) => boolean,
): AdministrationSubviewConfig<OrgaViewFrameContext> => {
  const title = config.title

  return {
    ...config,
    title: typeof title === "string" ? title : (context) => title(sharedContext(context)),
    getPages: (context) => (allowed(context) ? config.getPages(sharedContext(context)) : []),
  }
}

export const administrationConfig: AdministrationConfig<OrgaViewFrameContext> = {
  sections: [
    {
      id: "kehrblatt",
      title: "Kehrblatt",
      entries: [
        planning,
        dailyBriefing,
        announcementTemplates,
        district,
        customers,
        properties,
        orderTypes,
      ],
    },
    {
      id: "organization",
      title: "Organisation & Zugang",
      entries: [organization, users, auditLog],
    },
    {
      id: "communication",
      title: "KI & Kommunikation",
      entries: [
        withAccess(
          sharedAgents,
          (context) =>
            !isSubscriptionRestricted(context) &&
            context.tariffCapabilities.hasAiAgents &&
            context.permissions.allows("core.projects", "view"),
        ),
        withAccess(sharedAiRules, (context) => context.tariffCapabilities.hasSessionConfig),
        withAccess(
          sharedPhoneNumbers,
          (context) => canViewOrganizationSettings(context) && context.tariffCapabilities.hasPhone,
        ),
        withAccess(
          sharedContactProfiles,
          (context) =>
            context.permissions.isSuper &&
            context.tariffCapabilities.hasContactProfiles &&
            canViewOrganizationSettings(context),
        ),
      ],
    },
    {
      id: "billing",
      title: "Abrechnung & Dokumente",
      entries: [
        withAccess(sharedBilling, canViewBilling),
        withAccess(
          sharedLegalDocuments,
          (context) =>
            context.billingAvailable &&
            (context.currentUserRole === "admin" || context.currentUserRole === "customer_admin") &&
            context.permissions.allows("core.billing", "view"),
        ),
      ],
    },
  ],
}
